import React, {
    useRef,
    useState,
    useEffect,
    useCallback,
    lazy,
    Suspense,
    useMemo,
} from "react";

import {
    AiLearningLession,
    AiQuiz,
    AiVocabulary,
    AiVoice,
    CustomMessage,
    preImageItem,
} from "../../types";
import "./appAiAssist.scss";
import "../../css/styles.scss";
import { Input, message, Select } from 'antd';
import { nanoid } from "nanoid";
import { API_URL } from "../../constants";
import { marked } from "marked";
import clsx from "clsx";
import { fileOpen } from "../../data/filesystem";
import { getDataURL, resizeImageFile } from "../../data/blob";
import { AiModelConstants } from "../../utils/AiModelConstants";
import {
    AIAnalyzeImageIcon,
    AIChatIcon,
    AILearningIcon,
    AILessonPlanIcon,
    AIQuizIcon,
    AIReadArticleIcon,
    AISummarizeIcon,
    AITextImageIcon,
    AITranslateIcon,
    AIVocabularyIcon,
    IconClose,
    IconTextSpeech,
    IconCloseRightSideWin,
    IconCopy,
    IconImageAdd,
    IconLeftGoHistory,
    IconRemovePreImg,
    IconSendMsgDefault,
    IconUnion,
    AISportsIcon,
    AIJokesIcon,
    AIMathStoryIcon,
    AIRubricIcon,
    AISocailStoryIcon,
    AIFlowchartIcon,
    AIBlogTitleIcon,
    AIScience1Icon,
    AITextLevelerIcon,
    AIGrammarIcon,
    AIGenerationIcon,
    AIRecruitmentIcon,
    AIHistorycon,
    AITutorIcon,
    FileTranslationIcon,
    AIStrategicConsultingIcon,
    AIEducationalFileSummariserIcon,
    AIScienceLabDesignerIcon,
    AIAcademicContentIcon,
    AIYoutubeDataAnalysisIcon,
    AILessonMinutesSummaryIcon,
    AIReportCardCommentIcon,
    AIProjectBasedLearningIcon,
    AISatElaPracticeTestIcon,
    AIBlogInlineIcon
} from "../../components/icons";
import Spinner from "../../components/Spinner";
import { IconSend } from "../../components/newIcons";
import { AppAiTranslateAssist } from "../../components/AppAiTranslateAssist";
import { t } from "../../i18n";
import { AppAiChatTranslateWin } from "../../components/AppAiChatTranslateWin";
import { debounce } from "lodash";

const AISocailStory = lazy(() => import("./aiModel/AISocailStory").then(m => ({ default: m.AISocailStory })));
const AiSportsPractice = lazy(() => import("./aiModel/AiSportsPractice").then(m => ({ default: m.AiSportsPractice })));
const AITeacherJoke = lazy(() => import("./aiModel/AITeacherJoke").then(m => ({ default: m.AITeacherJoke })));
const AiMathStory = lazy(() => import("./aiModel/AIMathStory").then(m => ({ default: m.AiMathStory })));  // fix
const AiRubric = lazy(() => import("./aiModel/AiRubric").then(m => ({ default: m.AiRubric })));
const AiDiagram = lazy(() => import("./aiModel/AiDiagram").then(m => ({ default: m.AiDiagram })));
const AiScienceTeaching = lazy(() => import("./aiModel/AiScienceTeaching").then(m => ({ default: m.AiScienceTeaching })));
const AiBlogGenerator = lazy(() => import("./aiModel/AiBlogGenerator").then(m => ({ default: m.AiBlogGenerator })));
const AiTextLeveler = lazy(() => import("./aiModel/AiTextLeveler").then(m => ({ default: m.AiTextLeveler })));
const AiGrammerChecker = lazy(() => import("./aiModel/AiGrammerChecker").then(m => ({ default: m.AiGrammerChecker })));
const AiResearchAgent = lazy(() => import("./aiModel/AiResearchAgent").then(m => ({ default: m.AiResearchAgent })));
const AiHistorianQuestion = lazy(() => import("./aiModel/AiHistorianQuestion").then(m => ({ default: m.AiHistorianQuestion })));
const AiLanguageTutor = lazy(() => import("./aiModel/AiLanguageTutor").then(m => ({ default: m.AiLanguageTutor })));
const AiWorkSheet = lazy(() => import("./aiModel/AiWorkSheet").then(m => ({ default: m.AiWorkSheet })));
const AiStrategicConsulting = lazy(() => import("./aiModel/AiStrategicConsulting").then(m => ({ default: m.AiStrategicConsulting })));
const AiEducationalFileSummariser = lazy(() => import("./aiModel/AiEducationalFileSummariser").then(m => ({ default: m.AiEducationalFileSummariser })));
const AiLessonMinutesSummary = lazy(() => import("./aiModel/AiLessonMinutesSummary").then(m => ({ default: m.AiLessonMinutesSummary })));
const AiScienceLabDesigner = lazy(() => import("./aiModel/AiScienceLabDesigner").then(m => ({ default: m.AiScienceLabDesigner })));
const AiAcademicContent = lazy(() => import("./aiModel/AiAcademicContent").then(m => ({ default: m.AiAcademicContent })));
const AiYoutubeDataAnalysis = lazy(() => import("./aiModel/AiYoutubeDataAnalysis").then(m => ({ default: m.AiYoutubeDataAnalysis })));
const AiTranslateFile = lazy(() => import("./aiModel/AiTranslateFile").then(m => ({ default: m.AiTranslateFile })));  // tambah kembali
const AiReportCardComment = lazy(() => import("./aiModel/AiReportCardComment").then(m => ({ default: m.AiReportCardComment })));
const AiProjectBasedLearning = lazy(() => import("./aiModel/AiProjectBasedLearning").then(m => ({ default: m.AiProjectBasedLearning })));
const AiSatElaPracticeTest = lazy(() => import("./aiModel/AiSatElaPracticeTest").then(m => ({ default: m.AiSatElaPracticeTest })));
const AiBlogInline = lazy(() => import("./aiModel/AiBlogInline").then(m => ({ default: m.AiBlogInline })));

const getSidebarTitles = (): Record<string, string> => ({
    ai_assist: "Collab AI Assist",
    ai_generate_image: "Text to Image",
    ai_summarize: "Summarize",
    ai_analyze_image: "Analyze Image",
    ai_prompt_text: "AI Chat",
    ai_read_article: "Read Article",
    ai_quiz_and_assessment: "Quiz and Assessment",
    ai_vocabulary_list: t("labels.vocabulary_list"),
    ai_social_story: t("labels.social_story"),
    ai_sports_practice: t("labels.sports_practice"),
    ai_teacher_joke: t("labels.teacher_joke"),
    ai_math_story: t("labels.math_story"),
    ai_rubric_generator: t("labels.rubric_generator"),
    ai_worksheet_generator: t("labels.worksheet_generator"),
    ai_science_teaching: t("labels.science_teaching"),
    ai_blog_generator: t("labels.blog_generator"),
    ai_text_leveler: t("labels.text_leveler"),
    ai_grammer_checker: t("labels.grammer_checker"),
    ai_historian_question: t("labels.historian_question"),
    ai_language_tutor: t("labels.language_tutor"),
    ai_translate_file: t("labels.translate_file"),
    ai_research_agent: t("labels.research_agent"),
    ai_lesson_plan: t("labels.lesson_plan"),
    ai_learning_companion: t("labels.learning_companion"),
    ai_translate: t("labels.ai_translate"),
    ai_strategic_consulting: t("labels.strategic_consulting"),
    ai_diagram: t("labels.ai_diagram"),
    ai_educational_file_summariser: t("labels.educational_file_summariser"),
    ai_science_lab_designer: t("labels.science_lab_designer"),
    ai_academic_content: t("labels.academic_content"),
    ai_lesson_minutes_summary: t("labels.lesson_minutes_summary"),
    ai_report_card_comment: t("labels.report_card_comment"),
    ai_project_based_learning: t("labels.project_based_learning"),
    ai_sat_ela_practice_test: t("labels.sat_ela_practice_test"),
    ai_blog_inline: t("labels.blog_inline"),
});

const LANGUAGES = [
    { label: 'English', value: 'English' },
    { label: 'French', value: 'French' },
    { label: 'Spanish', value: 'Spanish' },
    { label: 'German', value: 'German' },
    { label: 'Italian', value: 'Italian' },
    { label: 'Dutch', value: 'Dutch' },
    { label: 'Swedish', value: 'Swedish' },
    { label: 'Czech', value: 'Czech' },
    { label: 'Turkish', value: 'Turkish' },
    { label: 'Russian', value: 'Russian' },
    { label: 'Greek', value: 'Greek' },
    { label: 'Hungarian', value: 'Hungarian' },
    { label: 'Irish', value: 'Irish' },
    { label: 'Japanese', value: 'Japanese' },
    { label: 'Korean', value: 'Korean' },
    { label: 'Chinese, Simplified', value: 'Chinese, Simplified' },
    { label: 'Chinese, Traditional', value: 'Chinese, Traditional' },
    { label: 'Malay', value: 'Malay' },
    { label: 'Indonesian', value: 'Indonesian' },
    { label: 'Thai', value: 'Thai' },
    { label: 'Vietnamese', value: 'Vietnamese' },
    { label: 'Filipino', value: 'Filipino' },
    { label: 'Khmer', value: 'Khmer' },
    { label: 'Hindi', value: 'Hindi' },
    { label: 'Tamil', value: 'Tamil' },
    { label: 'Kazakh', value: 'Kazakh' },
    { label: 'Polish', value: 'Polish' },
    { label: 'Portuguese', value: 'Portuguese' },
    { label: 'Arabic', value: 'Arabic' },
    { label: 'Afrikaans', value: 'Afrikaans' },
    { label: 'Swahili', value: 'Swahili' },
];

const SPEAKER_LANGUAGES = [
    { label: "English", value: "en-us" },
    { label: "Japanese", value: "ja" },
    { label: "Mandarin Chinese", value: "zh" },
    { label: "Spanish", value: "es" },
    { label: "French", value: "fr" },
    { label: "Hindi", value: "hi" },
    { label: "Italian", value: "it" },
    { label: "Portuguese (Brazil)", value: "pt-br" },
];

const PRE_IMAGES: preImageItem[] = [
    { imgUrl: "Default.jpg", modeName: "Default" },
    { imgUrl: "3D-Model.png", modeName: "3D Model" },
    { imgUrl: "Analog-Film.jpg", modeName: "Analog Film" },
    { imgUrl: "Anime.jpg", modeName: "Anime" },
    { imgUrl: "Cinematic.jpg", modeName: "Cinematic" },
    { imgUrl: "Comic-Book.jpg", modeName: "Comic Book" },
    { imgUrl: "Digital-Art.jpg", modeName: "Digital Art" },
    { imgUrl: "Enhance.jpg", modeName: "Enhance" },
    { imgUrl: "Fantasy-Art.jpg", modeName: "Fantasy Art" },
    { imgUrl: "Isometric.jpg", modeName: "Isometric" },
    { imgUrl: "Line-Art.jpg", modeName: "Line Art" },
    { imgUrl: "Low-Poly.jpg", modeName: "Low Poly" },
    { imgUrl: "Modelling-Compound.jpg", modeName: "Modelling Compound" },
    { imgUrl: "Neon-Punk.jpg", modeName: "Neon Punk" },
    { imgUrl: "Origami.jpg", modeName: "Origami" },
    { imgUrl: "Photographic.jpg", modeName: "Photographic" },
    { imgUrl: "Pixel-Art.jpg", modeName: "Pixel Art" },
    { imgUrl: "Tile-Texture.jpg", modeName: "Tile Texture" },
];

const { TextArea } = Input;

export interface AiImageInfo {
    url?: string;
    filename: string;
    subfolder: string;
    type: string;
    dataUrl: string;
    prompt: string
}

export interface SideOpenState {
    openRightSidebar: string;
}

export const AppAiAssistServer: React.FC<{
    currLangCode: string;
}> = ({
    currLangCode
}) => {

        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        let appIsLogin = 0;
        const val = urlParams?.get("appIsLogin");
        if (val !== null && val !== undefined) {
            appIsLogin = parseInt(val);
        }

        const call_source = urlParams?.get("call_source");

        const loginName = urlParams.get("username");
        const accessToken = urlParams.get("accessToken");

        let devicePixelRatio = window.devicePixelRatio;

        if (call_source && call_source === 'app') {
            if (devicePixelRatio == 3 || devicePixelRatio == 4) {
                devicePixelRatio = devicePixelRatio * 5 / 10
            }
            console.log(devicePixelRatio)
        } else {
            devicePixelRatio = window.devicePixelRatio === 2 ? 1.4 : 1;
        }

        document.documentElement.style.setProperty('--device-pixel-ratio', devicePixelRatio.toString());

        const [token, setToken] = useState(accessToken);
        const [username, setUsername] = useState(loginName);
        const [isLogin, setIsLogin] = useState(appIsLogin);
        const [chatMsgContent, setChatMsgContent] = useState<string>("");
        const [analyzeImagePre, setAnalyzeImagePre] = useState<string | null>(null);
        const [analyzeContent, setAnalyzeContent] = useState<string>("Analysis the image given");
        const [currentAIResponse, setCurrentAIResponse] = useState('');
        const [chatMsgs, setChatMsgs] = useState<CustomMessage[]>([]);
        const [analyzeMsgs, setAnalyzeMsgs] = useState<CustomMessage[]>([]);
        const [summarizeOriginInfo, setSummarizeOriginInfo] = useState<string>('');
        const [isRequestSummarize, setIsRequestSummarize] = useState<boolean>(false);
        const [isRequestQuiz, setIsRequestQuiz] = useState<boolean>(false);
        const [isRequestVocabulary, setIsRequestVocabulary] = useState<boolean>(false);
        const [isRequestLearning, setIsRequestLearning] = useState<boolean>(false);
        const [isRequestLessonPlan, setIsRequestLessonPlan] = useState<boolean>(false);
        const [isUploadImgToServer, setIsUploadImgToServer] = useState<boolean>(false);
        const [isRequestGenerateImg, setIsRequestGenerateImg] = useState<boolean>(false);
        const [isRequestGenerateAudio, setIsRequestGenerateAudio] = useState<boolean>(false);
        const [isDetecting, setIsDetecting] = useState(false);
        const [isSupportedLanguage, setIsSupportedLanguage] = useState(true);
        const [detectedLanguage, setDetectedLanguage] = useState<string | null>(null);
        const [summarizeAfterInfo, setSummarizeAfterInfo] = useState<string>('');
        const [generateImageType, setGenerateImageType] = useState<string>('Default');
        const [generateImageResult, setGenerateImageResult] = useState<AiImageInfo & { loaded: boolean }>({ loaded: false, prompt: '', filename: '', subfolder: '', type: '', dataUrl: '' });
        const [sideOpenState, setSideOpenState] = useState<SideOpenState>(() => {
            const saved = sessionStorage.getItem("ai_assist_last_open");
            return saved ? { openRightSidebar: saved } : { openRightSidebar: "ai_assist" };
        });
        const sideContentRef = useRef<HTMLDivElement>(null);
        const aiAssistScrollRef = useRef<HTMLDivElement>(null);
        const LIST_SCROLL_KEY = 'ai_assist_list_scroll_pos';
        const listScrollPos = {
            get current(): number {
                return parseFloat(sessionStorage.getItem(LIST_SCROLL_KEY) || '0');
            },
            set current(v: number) {
                sessionStorage.setItem(LIST_SCROLL_KEY, String(v));
            },
        };
        const [generateAudioResult, setGenerateAudioResult] = useState<AiVoice>({ input: '', result: '', language: 'en-us' });
        const [analyzeUploadImageInfo, setAnalyzeUploadImageInfo] = useState<AiImageInfo>({ prompt: '', filename: '', subfolder: '', type: '', dataUrl: '' });
        const [isActive, setActive] = useState(false);
        const [quizInfoPrompt, setQuizInfoPrompt] = useState<AiQuiz>({ subject: "", grade: "", topic: "", language: "", result: '' });
        const [learningCompanionInfo, setLearningCompanionInfo] = useState<AiLearningLession>({ country: '', question: '', grade: '', subject: '', language: '', result: '' });

        const [lessonPlanInfo, setLessonPlanInfo] = useState<AiLearningLession>({ country: '', question: '', grade: '', subject: '', language: '', result: '' });

        const [vocabularyListInfo, setVocabularyListInfo] = useState<AiVocabulary>({ text: '', grade: '', subject: '', language: '', result: '' });

        const sidebarTitles = useMemo(() => getSidebarTitles(), [currLangCode]);

        useEffect(() => {
            sessionStorage.setItem("ai_assist_last_open", sideOpenState.openRightSidebar);
        }, [sideOpenState]);

        // ── ASR states ──────────────────────────────────────────────────────────
        const [isRecording, setIsRecording] = useState<boolean>(false);
        const [isTranscribing, setIsTranscribing] = useState<boolean>(false);
        const mediaRecorderRef = useRef<MediaRecorder | null>(null);
        const audioChunksRef = useRef<Blob[]>([]);
        // ────────────────────────────────────────────────────────────────────────

        const [iframeQuizHasLoaded, setIframeQuizHasLoaded] = useState(false);
        const grades = AiModelConstants.getGrades();
        const sortedCountrys = AiModelConstants.getCountries().sort((a, b) => a.label.localeCompare(b.label));
        const sortedLanguages = [...LANGUAGES].sort((a, b) => a.label.localeCompare(b.label));

        async function onTouchEnd(e: any) {
            if (
                e.target?.tagName === "IMG" &&
                e.target.id != "driver-icon" &&
                !e.target?.getAttribute("data-isicon")
            ) {

                const data = JSON.stringify({
                    url: e.target.src,
                });
                sendToParent("insertImgToDoc", data);
            }
            e.stopPropagation();
        }

        useEffect(() => {
            window.addEventListener("touchend", onTouchEnd, false);
            return () => {
                window.removeEventListener("touchend", onTouchEnd);
            };
        }, []);

        const sendToParent = useCallback((type: string, data: any) => {
            if (window.AndroidInterface && call_source === "app") {
                const message = JSON.stringify({ event: type, data: data });
                window.AndroidInterface.sendMessage(message);
            } else {
                window.parent.postMessage(
                    {
                        type: type,
                        data: data,
                    },
                    "*",
                );
            }
        }, []);

        // ── ASR: SpeechRecognition ref ───────────────────────────────────────────
        const recognitionRef = useRef<any>(null);

        const shouldTranscribeRef = useRef<boolean>(false);
        const usingSpeechRecognitionRef = useRef<boolean>(false);

        const handleMicClick = async () => {
            if (isRecording) {
                shouldTranscribeRef.current = false; // user stop = don't transcribe
                recognitionRef.current?.stop();
                mediaRecorderRef.current?.stop();
                return;
            }

            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

                const mediaRecorder = new MediaRecorder(stream, {
                    mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
                        ? 'audio/webm;codecs=opus'
                        : 'audio/webm',
                });
                mediaRecorderRef.current = mediaRecorder;
                audioChunksRef.current = [];

                mediaRecorder.ondataavailable = (e) => {
                    if (e.data.size > 0) audioChunksRef.current.push(e.data);
                };

                mediaRecorder.onstop = async () => {
                    stream.getTracks().forEach((t) => t.stop());
                    setIsRecording(false);

                    // Skip ASR fetch if: not intended to transcribe, no audio, or browser SpeechRecognition already handled it
                    if (!shouldTranscribeRef.current || audioChunksRef.current.length === 0 || usingSpeechRecognitionRef.current) {
                        shouldTranscribeRef.current = false;
                        usingSpeechRecognitionRef.current = false;
                        return;
                    }
                    shouldTranscribeRef.current = false;
                    usingSpeechRecognitionRef.current = false;
                    setIsTranscribing(true);

                    const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                    const formData = new FormData();
                    formData.append('audio', audioBlob, 'recording.webm');
                    formData.append('language', 'auto');

                    try {
                        message.loading({ content: 'Finalizing...', key: 'asr' });

                        const response = await fetch(`${API_URL.aiHost}${API_URL.aiAutomaticSpeechRecognition}`, {
                            method: 'POST',
                            body: formData,
                        });

                        if (!response.ok) throw new Error('ASR request failed');

                        const data = await response.json();

                        if (data.success && data.data?.text) {
                            setChatMsgContent(data.data.text);
                            message.success({ content: 'Done!', key: 'asr' });
                        } else {
                            message.warning({ content: 'Could not transcribe audio', key: 'asr' });
                        }
                    } catch (err) {
                        console.error('ASR error:', err);
                        message.error({ content: 'Transcription failed', key: 'asr' });
                    } finally {
                        setIsTranscribing(false);
                    }
                };

                const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

                if (SpeechRecognition) {
                    usingSpeechRecognitionRef.current = true; // browser STT available, skip ASR fetch
                    const recognition = new SpeechRecognition();
                    recognitionRef.current = recognition;
                    recognition.continuous = true;
                    recognition.interimResults = true;
                    recognition.lang = 'en-US';

                    recognition.onresult = (event: any) => {
                        let interimText = '';
                        let finalText = '';

                        for (let i = event.resultIndex; i < event.results.length; i++) {
                            const transcript = event.results[i][0].transcript;
                            if (event.results[i].isFinal) {
                                finalText += transcript;
                            } else {
                                interimText += transcript;
                            }
                        }

                        setChatMsgContent(finalText || interimText);
                    };

                    recognition.onerror = (event: any) => {
                        console.warn('Speech recognition error:', event.error);
                    };

                    recognition.start();
                } else {
                    usingSpeechRecognitionRef.current = false; // no browser STT, use ASR fetch
                }

                shouldTranscribeRef.current = true; // will transcribe on stop unless overridden
                mediaRecorder.start();
                setIsRecording(true);

            } catch (err) {
                console.error('Mic access error:', err);
                message.error('Microphone access denied. Please allow mic permission.');
            }
        };
        // ────────────────────────────────────────────────────────────────────────

        const sendAiSummarize = () => {
            if (summarizeOriginInfo == '') {
                return
            }
            setSummarizeAfterInfo('')
            setIsRequestSummarize(true)
            fetch(`${API_URL.aiHost}/aiapi/v1/summarize`, {
                method: "POST",
                body: JSON.stringify({ text: summarizeOriginInfo }),
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            })
                .then((response) => {
                    if (response.ok) {
                        return response.json();
                    }
                })
                .then((data) => {
                    if (data.message == "Successful" && data.data && data.data.content != '') {
                        setSummarizeAfterInfo(data.data.content)
                    } else {
                        message.warning("The network is busy, please try again later")
                    }

                })
                .catch((err) => {
                    message.error("Please try again later")
                    console.log(err);
                });
        };


        const removeMsg = (item: CustomMessage, type: string) => {
            if (type === "user") {
                item.isDelUserMsg = true
            } else {
                item.aiMsg = ''
                item.isDelAiMsg = true
                setCurrentAIResponse("")
            }

            if (item.isDelUserMsg && item.isDelAiMsg) {
                setChatMsgs([
                    ...chatMsgs.filter((msg) => msg.id != item.id)
                ]);
            } else {
                setChatMsgs([
                    ...chatMsgs.filter((msg) => msg.id != item.id),
                    item,
                ]);
            }
        }


        const sendAiQuiz = () => {
            if (quizInfoPrompt.subject == '') {
                message.warning("subject value can not be empty")
                return
            }
            if (quizInfoPrompt.grade == '') {
                message.warning("grade value can not be empty")
                return
            }
            if (quizInfoPrompt.topic == '') {
                message.warning("topic value can not be empty")
                return
            }
            if (quizInfoPrompt.language == '') {
                message.warning("language value can not be empty")
                return
            }
            setIsRequestQuiz(true)
            fetch(`${API_URL.host}${API_URL.aiCompletionMessages}`, {
                method: "POST",
                body: JSON.stringify({ subject: quizInfoPrompt.subject, grade: quizInfoPrompt.grade, topic: quizInfoPrompt.topic, language: quizInfoPrompt.language }),
                headers: {
                    Authorization: `Bearer ${token}`,

                },
            })
                .then((response) => {
                    if (response.ok) {
                        return response.json();
                    }
                })
                .then((data) => {
                    if (data.code == 200 && data.data && data.data != '') {
                        setQuizInfoPrompt({
                            ...quizInfoPrompt,
                            result: data.data
                        })
                    } else {
                        setIsRequestQuiz(false)
                        message.warning("The network is busy, please try again later")
                    }

                })
                .catch((err) => {
                    message.error("Please try again later")
                    setIsRequestQuiz(false)
                    console.log(err);
                });
        };


        const sendAiVocabulary = () => {
            if (vocabularyListInfo.subject == '') {
                message.warning("subject value can not be empty")
                return
            }
            if (vocabularyListInfo.grade == '') {
                message.warning("grade value can not be empty")
                return
            }
            if (vocabularyListInfo.text == '') {
                message.warning("text value can not be empty")
                return
            }
            if (vocabularyListInfo.language == '') {
                message.warning("language value can not be empty")
                return
            }
            setIsRequestVocabulary(true)
            fetch(`${API_URL.host}${API_URL.aiVocabularyGenerator}`, {
                method: "POST",
                body: JSON.stringify({ subject: vocabularyListInfo.subject, grade: vocabularyListInfo.grade, text: vocabularyListInfo.text, language: vocabularyListInfo.language }),
                headers: {
                    Authorization: `Bearer ${token}`,

                    'Content-Type': 'application/json',
                },
            })
                .then((response) => {
                    if (response.ok) {
                        return response.json();
                    }
                })
                .then((data) => {
                    if (data.code == 200 && data.data && data.data != '') {
                        setVocabularyListInfo({
                            ...vocabularyListInfo,
                            result: data.data
                        })
                    } else {
                        setIsRequestVocabulary(false)
                        message.warning("The network is busy, please try again later")
                    }

                })
                .catch((err) => {
                    message.error("Please try again later")
                    setIsRequestVocabulary(false)
                    console.log(err);
                });
        };

        const sendAiLearningCompanion = () => {
            if (learningCompanionInfo.country == '') {
                message.warning("country value can not be empty")
                return
            }
            if (learningCompanionInfo.grade == '') {
                message.warning("grade value can not be empty")
                return
            }

            if (learningCompanionInfo.language == '') {
                message.warning("language value can not be empty")
                return
            }
            if (learningCompanionInfo.subject == '') {
                message.warning("subject value can not be empty")
                return
            }
            if (learningCompanionInfo.question == '') {
                message.warning("question value can not be empty")
                return
            }
            setIsRequestLearning(true)
            fetch(`${API_URL.host}${API_URL.aiLearningCompanion}`, {
                method: "POST",
                body: JSON.stringify({ country: learningCompanionInfo.country, subject: learningCompanionInfo.subject, grade: learningCompanionInfo.grade, question: learningCompanionInfo.question, language: learningCompanionInfo.language }),
                headers: {
                    Authorization: `Bearer ${token}`,

                    'Content-Type': 'application/json',
                },
            })
                .then((response) => {
                    if (response.ok) {
                        return response.json();
                    }
                })
                .then((data) => {
                    if (data.code == 200 && data.data && data.data != '') {
                        setLearningCompanionInfo({
                            ...learningCompanionInfo,
                            result: data.data
                        })
                    } else {
                        setIsRequestLearning(false)
                        message.warning("The network is busy, please try again later")
                    }

                })
                .catch((err) => {
                    message.error("Please try again later")
                    setIsRequestLearning(false)
                    console.log(err);
                });
        };


        const sendAiLessionPlan = () => {
            if (lessonPlanInfo.country == '') {
                message.warning("country value can not be empty")
                return
            }
            if (lessonPlanInfo.grade == '') {
                message.warning("grade value can not be empty")
                return
            }

            if (lessonPlanInfo.language == '') {
                message.warning("language value can not be empty")
                return
            }
            if (lessonPlanInfo.subject == '') {
                message.warning("subject value can not be empty")
                return
            }
            if (lessonPlanInfo.question == '') {
                message.warning("question value can not be empty")
                return
            }
            setIsRequestLessonPlan(true)
            fetch(`${API_URL.host}${API_URL.aiLessonPlan}`, {
                method: "POST",
                body: JSON.stringify({ country: lessonPlanInfo.country, subject: lessonPlanInfo.subject, grade: lessonPlanInfo.grade, question: lessonPlanInfo.question, language: lessonPlanInfo.language }),
                headers: {
                    Authorization: `Bearer ${token}`,

                    'Content-Type': 'application/json',
                },
            })
                .then((response) => {
                    if (response.ok) {
                        return response.json();
                    }
                })
                .then((data) => {
                    if (data.code == 200 && data.data && data.data != '') {
                        setLessonPlanInfo({
                            ...lessonPlanInfo,
                            result: data.data
                        })
                    } else {
                        setIsRequestLessonPlan(false)
                        message.warning("The network is busy, please try again later")
                    }

                })
                .catch((err) => {
                    message.error("Please try again later")
                    setIsRequestLessonPlan(false)
                    console.log(err);
                });
        };

        const detectLanguage = async (inputText: string) => {
            if (!inputText.trim()) {
                setDetectedLanguage(null);
                setIsSupportedLanguage(true);
                return;
            }

            setIsDetecting(true);
            try {
                const response = await fetch(
                    `${API_URL.aiHost}/aiapi/v1/reclanguage`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ prompt: inputText }),
                    }
                );

                if (!response.ok) throw new Error("Failed to detect language");

                const data = await response.json();

                let detectedLang = data.data.lang;

                console.log("detectedLang === ", detectedLang);

                const normalizationMap: Record<string, string> = {
                    en: "en-us",
                    pt: "pt-br",
                    zh: "zh",
                    es: "es",
                    fr: "fr",
                    hi: "hi",
                    it: "it",
                    ja: "ja",
                };
                const normalizedLang = normalizationMap[detectedLang] || detectedLang;

                setDetectedLanguage(normalizedLang);

                const isSupported = SPEAKER_LANGUAGES.some(
                    (lang) => lang.value.toLowerCase() === normalizedLang
                );
                setIsSupportedLanguage(isSupported);

                if (isSupported) {
                    setGenerateAudioResult((prev) => ({
                        ...prev,
                        language: normalizedLang,
                    }));
                } else {
                    setGenerateAudioResult((prev) => ({
                        ...prev,
                        language: "not-supported",
                    }));
                }
            } catch (err) {
                console.error("Language detection failed:", err);
                setDetectedLanguage(null);
                setIsSupportedLanguage(false);
                setGenerateAudioResult((prev) => ({
                    ...prev,
                    language: "not-supported",
                }));
            } finally {
                setIsDetecting(false);
            }
        };

        const handleTextChange = debounce((text: string) => detectLanguage(text), 800);

        const sendAiGenerateAudio = () => {
            const language = generateAudioResult.language || "en-us";
            const input = (generateAudioResult.input || "").trim();

            if (!language) {
                message.warning(t("labels.ai_language_empty"))
                return
            }
            if (!input) {
                message.warning(t("labels.ai_text_empty"))
                return
            }
            setIsRequestGenerateAudio(true);

            setGenerateAudioResult(prev => ({ ...prev, result: '' }));

            const payload = {
                input,
                language
            };

            fetch(`${API_URL.aiVoice}`, {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            })
                .then(response => {
                    if (!response.ok) {
                        throw new Error('Failed to generate audio');
                    }
                    return response.blob();
                })
                .then(blob => {
                    const url = URL.createObjectURL(blob);
                    setGenerateAudioResult(prev => ({ ...prev, result: url }));
                })
                .catch(err => {
                    console.error('Audio fetch failed:', err);
                    alert('Failed to generate audio');
                })
                .finally(() => {
                    setIsRequestGenerateAudio(false);
                });
        };


        const sendAiGenerateImage = () => {
            if (generateImageResult.prompt == '') {
                return
            }
            setIsRequestGenerateImg(true)
            setGenerateImageResult({ ...generateImageResult, url: "", filename: '', subfolder: '', type: '', loaded: false })
            let prompt = generateImageResult.prompt;
            if (generateImageType != "Default") {
                prompt = generateImageType + " style:" + prompt
            }
            fetch(`${API_URL.host}${API_URL.aiGenerateImg}`, {
                method: "POST",
                body: JSON.stringify({ prompt: prompt }),
                headers: {
                    Authorization: `Bearer ${token}`,

                    'Content-Type': 'application/json',
                },
            })
                .then((response) => {
                    if (response.ok) {
                        return response.json();
                    }
                })
                .then((data) => {
                    if (data.code == 200) {
                        setGenerateImageResult({
                            ...generateImageResult,
                            url: data.data,

                        })
                    }
                    setIsRequestGenerateImg(false)
                })
                .catch((err) => {
                    setIsRequestGenerateImg(false)
                    console.log(err);
                });
        };


        const sendAiAnalyseImg = () => {
            if (analyzeContent == "" || !analyzeUploadImageInfo.dataUrl || analyzeUploadImageInfo.dataUrl == "") {
                return
            }

            const msg = {
                id: nanoid(),
                userMsg: analyzeContent,
                aiMsg: '',
                imageInfo: analyzeUploadImageInfo,
                imageDataUrlPre: analyzeImagePre,
                isDelUserMsg: false,
                isDelAiMsg: false,
            };

            setAnalyzeImagePre(null)
            setAnalyzeUploadImageInfo({ ...analyzeUploadImageInfo, url: "", filename: '', subfolder: '', type: '' })
            setAnalyzeMsgs([...analyzeMsgs, msg]);

            fetch(`${API_URL.aiHost}/aiapi/v1/image/analyze`, {
                method: "POST",
                body: JSON.stringify({ prompt: msg.userMsg, image: msg.imageInfo.dataUrl }),
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            })
                .then((response) => {
                    if (response.ok) {
                        return response.json();
                    }
                })
                .then((data) => {
                    if (data.message == 'Successful' && data.data && data.data.content != '') {
                        const aiMessage = data.data.content;
                        msg.aiMsg = aiMessage
                        setAnalyzeMsgs([...analyzeMsgs, msg]);
                    } else {
                        message.warning(t("labels.ai_network_busy"))
                    }

                })
                .catch((err) => {
                    message.warning("Tplease try again later")
                    console.log(err);
                });
        };



        const sendAiMessage = (sendMsg: string) => {
            if (sendMsg == "") {
                return
            }

            if (isRecording) {
                shouldTranscribeRef.current = true; // generate result = transcribe first
                recognitionRef.current?.stop();
                mediaRecorderRef.current?.stop();
            }

            setCurrentAIResponse("")

            const msg = {
                id: nanoid(),
                userMsg: sendMsg,
                aiMsg: '',
                aiMsgPre: '',
                isDelUserMsg: false,
                isDelAiMsg: false,
            };

            setChatMsgContent("")
            setChatMsgs([...chatMsgs, msg]);

            fetch(`${API_URL.aiHost}/aiapi/v1/chat`, {
                method: "POST",
                body: JSON.stringify({ prompt: msg.userMsg }),
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            })
                .then((response) => {
                    if (response.ok) {
                        return response.json();
                    }
                })
                .then((data) => {
                    if (data.message == "Successful" && data.data && data.data.content != '') {
                        const aiMessage = data.data.content;
                        msg.aiMsgPre = aiMessage;
                        setChatMsgs([...chatMsgs, msg]);
                        displayAIMessage(aiMessage, msg.id);
                    } else {
                        message.warning("The network is busy, please try again later")
                    }

                })
                .catch((err) => {
                    message.warning("please try again later")
                    console.log(err);
                });
        };


        const uploadImage = async () => {

            try {
                const imageFile = await fileOpen({
                    description: "Image",
                    extensions: ["jpg", "png", "svg", "gif"],
                });

                const cursorImageSizePx = 320;
                const imagePreview = await resizeImageFile(imageFile, {
                    maxWidthOrHeight: cursorImageSizePx,
                });

                let previewDataURL = await getDataURL(imagePreview);
                setAnalyzeImagePre(previewDataURL)

                setIsUploadImgToServer(true)

                const formData = new FormData();
                formData.append('image', imageFile);

                fetch(`${API_URL.aiHost}/aiapi/v1/image`, {
                    method: 'POST',
                    body: formData
                }).then((response) => {
                    if (response.ok) {
                        return response.json();
                    }
                })
                    .then((data) => {
                        if (data.message == 'Successful') {
                            setAnalyzeUploadImageInfo({
                                ...analyzeUploadImageInfo,
                                dataUrl: data.data.data,
                            })
                        }

                        setIsUploadImgToServer(false)

                    })
                    .catch((err) => {
                        setIsUploadImgToServer(false)
                        console.log(err);
                    });


            } catch (error: any) {
                if (error.name !== "AbortError") {
                    console.error(error);
                } else {
                    console.warn(error);
                }


            }
        };

        const displayAIMessage = (message: string, msgId: string) => {
            let index = -1;
            const interval = setInterval(() => {
                if (index < message.length - 1) {
                    setCurrentAIResponse((prev) => prev + message[index]);
                    index++;
                } else {
                    clearInterval(interval);
                    setChatMsgs((prevMsgs) =>
                        prevMsgs.map((msg) =>
                            msg.id === msgId ? { ...msg, aiMsg: message } : msg
                        )
                    );
                }
            }, 10);

        };

        const renderAiAssist = () => {
            return <div className="ai-assist" ref={aiAssistScrollRef}>

                <div className="txt-label">
                    {t("labels.hi")} <span className="user-name">{isLogin == 1 && username}</span>{t("labels.help")}
                </div>

                <div className="actions-group">
                    <div
                        className="action-item"
                        onClick={() => (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_prompt_text" });
                            })()}
                    >
                        <div className="action-icon">
                            {AIChatIcon}
                        </div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.ai_chat")}</div>
                            <div className="action-desc">{t("labels.ai_chat_desc")}</div>
                        </div>
                    </div>

                    <div
                        className="action-item"
                        onClick={() => (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_generate_image" });
                            })()}
                    >
                        <div className="action-icon">
                            {AITextImageIcon}
                        </div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.text_to_image")}</div>
                            <div className="action-desc">{t("labels.text_to_image_desc")}</div>
                        </div>
                    </div>

                    <div
                        className="action-item"
                        onClick={() => (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_analyze_image" });
                            })()}
                    >
                        <div className="action-icon">
                            {AIAnalyzeImageIcon}
                        </div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.analyze_image")}</div>
                            <div className="action-desc">{t("labels.analyze_image_desc")}</div>
                        </div>
                    </div>

                    <div
                        className="action-item"
                        onClick={() => {
                            (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_summarize" });
                            })()
                        }}
                    >
                        <div className="action-icon">
                            {AISummarizeIcon}
                        </div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.summarize")}</div>
                            <div className="action-desc">{t("labels.summarize_desc")}</div>
                        </div>
                    </div>

                    <div
                        className="action-item"
                        onClick={() => {
                            (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_read_article" });
                            })()
                        }}
                    >
                        <div className="action-icon">
                            {AIReadArticleIcon}
                        </div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.read_article")}</div>
                            <div className="action-desc">{t("labels.read_article_desc")}</div>
                        </div>
                    </div>

                    <div
                        className="action-item"
                        onClick={() => {
                            (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_lesson_plan" });
                            })()

                        }}
                    >
                        <div className="action-icon">
                            {AILessonPlanIcon}
                        </div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.lesson_plan")}</div>
                            <div className="action-desc">{t("labels.lesson_plan_desc")}</div>
                        </div>
                    </div>

                    <div
                        className="action-item"
                        onClick={() => {

                            (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_quiz_and_assessment" });
                            })()
                        }}
                    >
                        <div className="action-icon">
                            {AIQuizIcon}
                        </div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.quiz_and_assessment")}</div>
                            <div className="action-desc">{t("labels.quiz_and_assessment_desc")}</div>
                        </div>
                    </div>

                    <div
                        className="action-item"
                        onClick={() => {

                            (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_learning_companion" });
                            })()

                        }}
                    >
                        <div className="action-icon">
                            {AILearningIcon}
                        </div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.learning_companion")}</div>
                            <div className="action-desc">{t("labels.learning_companion_desc")}</div>
                        </div>
                    </div>

                    <div
                        className="action-item"
                        onClick={() => {

                            (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_vocabulary_list" });
                            })()

                        }}
                    >
                        <div className="action-icon">
                            {AIVocabularyIcon}
                        </div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.vocabulary_list")}</div>
                            <div className="action-desc">{t("labels.vocabulary_list_desc")}</div>
                        </div>
                    </div>

                    <div
                        className="action-item"
                        onClick={() => {

                            (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_sports_practice" });
                            })()

                        }}
                    >
                        <div className="action-icon">{AISportsIcon}</div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.sports_practice")}</div>
                            <div className="action-desc">
                                {t("labels.sports_practice_desc")}
                            </div>
                        </div>
                    </div>

                    <div
                        className="action-item"
                        onClick={() => {

                            (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_teacher_joke" });
                            })()

                        }}
                    >
                        <div className="action-icon">{AIJokesIcon}</div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.teacher_joke")}</div>
                            <div className="action-desc">
                                {t("labels.teacher_joke_desc")}
                            </div>
                        </div>
                    </div>

                    <div
                        className="action-item"
                        onClick={() => {

                            (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_math_story" });
                            })()

                        }}
                    >
                        <div className="action-icon">{AIMathStoryIcon}</div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.math_story")}</div>
                            <div className="action-desc">
                                {t("labels.math_story_desc")}
                            </div>
                        </div>
                    </div>

                    <div
                        className="action-item"
                        onClick={() => {

                            (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_rubric_generator" });
                            })()

                        }}
                    >
                        <div className="action-icon">{AIRubricIcon}</div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.rubric_generator")}</div>
                            <div className="action-desc">
                                {t("labels.rubric_generator_desc")}
                            </div>
                        </div>
                    </div>

                    <div
                        className="action-item"
                        onClick={() => {

                            (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_social_story" });
                            })()

                        }}
                    >
                        <div className="action-icon">{AISocailStoryIcon}</div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.social_story")}</div>
                            <div className="action-desc">
                                {t("labels.social_story_desc")}
                            </div>
                        </div>
                    </div>

                    <div
                        className="action-item"
                        onClick={() => (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_translate" });
                            })()}
                    >
                        <div className="action-icon">
                            {AITranslateIcon}
                        </div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.ai_translate")}</div>
                            <div className="action-desc">{t("labels.ai_translate_desc")}</div>
                        </div>
                    </div>

                    <div
                        className="action-item"
                        onClick={() =>
                            (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_diagram" });
                            })()
                        }
                    >
                        <div className="action-icon">{AIFlowchartIcon}</div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.diagram_flowchart")}</div>
                            <div className="action-desc">
                                {t("labels.diagram_flowchart_desc")}
                            </div>
                        </div>
                    </div>

                    <div
                        className="action-item"
                        onClick={() =>
                            (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_blog_generator" });
                            })()
                        }
                    >
                        <div className="action-icon">{AIBlogTitleIcon}</div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.blog_generator")}</div>
                            <div className="action-desc">
                                {t("labels.blog_generator_desc")}
                            </div>
                        </div>
                    </div>

                    <div
                        className="action-item"
                        onClick={() =>
                            (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_science_teaching" });
                            })()
                        }
                    >
                        <div className="action-icon">{AIScience1Icon}</div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.science_teaching")}</div>
                            <div className="action-desc">
                                {t("labels.science_teaching_desc")}
                            </div>
                        </div>
                    </div>

                    <div
                        className="action-item"
                        onClick={() =>
                            (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_text_leveler" });
                            })()
                        }
                    >
                        <div className="action-icon">{AITextLevelerIcon}</div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.text_leveler")}</div>
                            <div className="action-desc">
                                {t("labels.text_leveler_desc")}
                            </div>
                        </div>
                    </div>

                    <div
                        className="action-item"
                        onClick={() =>
                            (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_grammer_checker" });
                            })()
                        }
                    >
                        <div className="action-icon">{AIGrammarIcon}</div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.grammer_checker")}</div>
                            <div className="action-desc">
                                {t("labels.grammer_checker_desc")}
                            </div>
                        </div>
                    </div>

                    <div
                        className="action-item"
                        onClick={() =>
                            (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_worksheet_generator" });
                            })()
                        }
                    >
                        <div className="action-icon">{AIGenerationIcon}</div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.worksheet_generator")}</div>
                            <div className="action-desc">
                                {t("labels.worksheet_generator_desc")}
                            </div>
                        </div>
                    </div>

                    <div
                        className="action-item"
                        onClick={() =>
                            (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_research_agent" });
                            })()
                        }
                    >
                        <div className="action-icon">{AIRecruitmentIcon}</div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.research_agent")}</div>
                            <div className="action-desc">
                                {t("labels.research_agent_desc")}
                            </div>
                        </div>
                    </div>

                    <div
                        className="action-item"
                        onClick={() =>
                            (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_historian_question" });
                            })()
                        }
                    >
                        <div className="action-icon">{AIHistorycon}</div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.historian_question")}</div>
                            <div className="action-desc">
                                {t("labels.historian_question_desc")}
                            </div>
                        </div>
                    </div>

                    <div
                        className="action-item"
                        onClick={() =>
                            (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_language_tutor" });
                            })()
                        }
                    >
                        <div className="action-icon">{AITutorIcon}</div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.language_tutor")}</div>
                            <div className="action-desc">
                                {t("labels.language_tutor_desc")}
                            </div>
                        </div>
                    </div>

                    <div
                        className="action-item"
                        onClick={() =>
                            (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_translate_file" });
                            })()
                        }
                    >
                        <div className="action-icon">{FileTranslationIcon}</div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.translate_file")}</div>
                            <div className="action-desc">
                                {t("labels.translate_file_desc")}
                            </div>
                        </div>
                    </div>

                    <div
                        className="action-item"
                        onClick={() =>
                            (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_strategic_consulting" });
                            })()
                        }
                    >
                        <div className="action-icon">{AIStrategicConsultingIcon}</div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.strategic_consulting")}</div>
                            <div className="action-desc">
                                {t("labels.strategic_consulting_desc")}
                            </div>
                        </div>
                    </div>

                    <div
                        className="action-item"
                        onClick={() => (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_educational_file_summariser" });
                            })()}
                        >
                        <div className="action-icon">
                            {AIEducationalFileSummariserIcon}
                        </div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.educational_file_summariser")}</div>
                            <div className="action-desc">{t("labels.educational_file_summariser_desc")}</div>
                        </div>
                    </div>

                    <div
                        className="action-item"
                        onClick={() => (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_science_lab_designer" });
                            })()}
                    >
                        <div className="action-icon">
                            {AIScienceLabDesignerIcon}
                        </div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.science_lab_designer")}</div>
                            <div className="action-desc">{t("labels.science_lab_designer_desc")}</div>
                        </div>
                    </div>

                    <div
                        className="action-item"
                        onClick={() => (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_academic_content" });
                            })()}
                    >
                        <div className="action-icon">
                            {AIAcademicContentIcon}
                        </div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.academic_content")}</div>
                            <div className="action-desc">{t("labels.academic_content_desc")}</div>
                        </div>
                    </div>

                    {/* <div
                        className="action-item"
                        onClick={() => (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_youtube_data_analysis" });
                            })()}
                        >
                        <div className="action-icon">
                            {AIYoutubeDataAnalysisIcon}
                        </div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.youtube_data_analysis")}</div>
                            <div className="action-desc">{t("labels.youtube_data_analysis_desc")}</div>
                        </div>
                    </div> */}

                    <div
                        className="action-item"
                        onClick={() => (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_lesson_minutes_summary" });
                            })()}
                        >
                        <div className="action-icon">
                            {AILessonMinutesSummaryIcon}
                        </div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.lesson_minutes_summary")}</div>
                            <div className="action-desc">{t("labels.lesson_minutes_summary_desc")}</div>
                        </div>
                    </div>

                    <div
                        className="action-item"
                        onClick={() => (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_report_card_comment" });
                            })()}
                    >
                        <div className="action-icon">{AIReportCardCommentIcon}</div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.report_card_comment")}</div>
                            <div className="action-desc">{t("labels.report_card_comment_desc")}</div>
                        </div>
                    </div>

                    <div
                        className="action-item"
                        onClick={() => (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_project_based_learning" });
                            })()}
                    >
                        <div className="action-icon">{AIProjectBasedLearningIcon}</div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.project_based_learning")}</div>
                            <div className="action-desc">{t("labels.project_based_learning_desc")}</div>
                        </div>
                    </div>

                    <div
                        className="action-item"
                        onClick={() => (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_sat_ela_practice_test" });
                            })()}
                    >
                        <div className="action-icon">{AISatElaPracticeTestIcon}</div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.sat_ela_practice_test")}</div>
                            <div className="action-desc">{t("labels.sat_ela_practice_test_desc")}</div>
                        </div>
                    </div>

                    <div
                        className="action-item"
                        onClick={() => (() => {
                                listScrollPos.current = aiAssistScrollRef.current?.scrollTop ?? 0;
                                setSideOpenState({ openRightSidebar: "ai_blog_inline" });
                            })()}
                    >
                        <div className="action-icon">{AIBlogInlineIcon}</div>
                        <div className="action-text">
                            <div className="action-label">{t("labels.blog_inline")}</div>
                            <div className="action-desc">{t("labels.blog_inline_desc")}</div>
                        </div>
                    </div>

                </div>

            </div>;
        }


        const handleOnClick = (message: CustomMessage) => {
            setActive(!isActive);
        };

        const renderAiChat = () => {
            return (<div className="ai-chat">
                <div className="content-main">

                    <div className="message-box">
                        {chatMsgs.map((item, key) => {
                            return (
                                <>
                                    {(!item.isDelUserMsg || !item.isDelAiMsg) && (
                                        <div key={`ai_item_${item.id}`} className="item">
                                            {item.userMsg !== "" && (
                                                <>
                                                    <div className="item-user">
                                                        <div className="buttons">
                                                            <div>

                                                                <a
                                                                    href={undefined}
                                                                    onClick={async () => {
                                                                        let text = item.userMsg?.trim();
                                                                        if (!text) {
                                                                            message.warning("No AI message to read.");
                                                                            return;
                                                                        }
                                                                        if (item.isUserAudioPlaying) {
                                                                            return;
                                                                        }
                                                                        try {
                                                                            item.isUserAudioPlaying = true;
                                                                            message.loading({ content: "Generating audio...", key: "tts" });
                                                                            const payload = { input: text, language: "" };
                                                                            const ttsResponse = await fetch(`${API_URL.aiVoice}`, {
                                                                                method: "POST",
                                                                                headers: { "Content-Type": "application/json" },
                                                                                body: JSON.stringify(payload),
                                                                            });

                                                                            if (!ttsResponse.ok) throw new Error("TTS generation failed");

                                                                            const blob = await ttsResponse.blob();
                                                                            const url = URL.createObjectURL(blob);
                                                                            setChatMsgs(prevMsgs =>
                                                                                prevMsgs.map(msg =>
                                                                                    msg.id === item.id ? { ...msg, userTTLURL: url } : msg
                                                                                )
                                                                            );

                                                                        } catch (err) {
                                                                            console.error("This language is not supported now", err);
                                                                            item.isUserAudioPlaying = false;
                                                                            message.error({ content: "This language is not supported now", key: "tts" });
                                                                        }
                                                                    }}
                                                                >
                                                                    {IconTextSpeech}
                                                                </a>
                                                            </div>

                                                            <div>
                                                                <a
                                                                    href={undefined}
                                                                    onClick={() => removeMsg(item, "user")}
                                                                >
                                                                    {IconClose}
                                                                </a>
                                                            </div>

                                                            <div>
                                                                <AppAiChatTranslateWin translateResult={(translatedText) => {
                                                                    item.userTranslateMsg = translatedText
                                                                    handleOnClick(item)
                                                                }} textValue={item.userMsg} />
                                                            </div>
                                                            <div>
                                                                {(item.userTranslateMsg && item.userTranslateMsg != undefined && item.userTranslateMsg != '') && (<a
                                                                    style={{ paddingTop: "3px" }}
                                                                    href={undefined}
                                                                    onClick={() => {
                                                                        item.userTranslateMsg && sendAiMessage(item.userTranslateMsg)
                                                                    }}
                                                                >
                                                                    {IconSend}
                                                                </a>)}
                                                            </div>



                                                        </div>
                                                        <div className="content">
                                                            {item.userMsg}
                                                            {item.userTranslateMsg &&
                                                                item.userTranslateMsg != undefined &&
                                                                item.userTranslateMsg != "" && (
                                                                    <div
                                                                        dangerouslySetInnerHTML={{
                                                                            __html: marked(
                                                                                item.userTranslateMsg,
                                                                            ) as string,
                                                                        }}
                                                                    ></div>
                                                                )}
                                                        </div>
                                                        <div className="avatar">
                                                            <div className="icon">I</div>
                                                        </div>
                                                    </div>

                                                    {item.userTTLURL && (<div className="user-show-audio">

                                                        <audio controls>
                                                            <source src={item.userTTLURL} type="audio/wav" />
                                                            Your browser does not support the audio element.
                                                        </audio>

                                                    </div>)}
                                                </>
                                            )}

                                            {!item.isDelAiMsg && (
                                                <>
                                                    <div className="item-ai">
                                                        <div className="avatar">
                                                            <div className="icon">AI</div>
                                                        </div>

                                                        <div className="ai-content">
                                                            <div className="ai-content-text">
                                                                {!item.aiMsg && currentAIResponse == "" ? (
                                                                    <Spinner />
                                                                ) : (
                                                                    <div
                                                                        dangerouslySetInnerHTML={{
                                                                            __html: marked(
                                                                                item.id ===
                                                                                    chatMsgs[chatMsgs.length - 1].id &&
                                                                                    currentAIResponse != ""
                                                                                    ? currentAIResponse
                                                                                    : (item.aiMsg || ""),
                                                                            ) as string,
                                                                        }}
                                                                    ></div>
                                                                )}
                                                                {item.aiTranslateMsg &&
                                                                    item.aiTranslateMsg != undefined && (
                                                                        <div
                                                                            dangerouslySetInnerHTML={{
                                                                                __html: marked(
                                                                                    item.aiTranslateMsg,
                                                                                ) as string,
                                                                            }}
                                                                        ></div>
                                                                    )}
                                                            </div>



                                                            <div className="buttons">

                                                                <div>

                                                                    <a
                                                                        href={undefined}
                                                                        onClick={async () => {
                                                                            const text = item.aiMsg?.trim();
                                                                            if (!text) {
                                                                                message.warning("No AI message to read.");
                                                                                return;
                                                                            }
                                                                            if (item.isAiAudioPlaying) {
                                                                                return;
                                                                            }
                                                                            try {
                                                                                item.isAiAudioPlaying = true;
                                                                                message.loading({ content: "Generating audio...", key: "tts" });
                                                                                const payload = { input: text, language: "" };
                                                                                const ttsResponse = await fetch(`${API_URL.aiVoice}`, {
                                                                                    method: "POST",
                                                                                    headers: { "Content-Type": "application/json" },
                                                                                    body: JSON.stringify(payload),
                                                                                });

                                                                                if (!ttsResponse.ok) throw new Error("TTS generation failed");

                                                                                const blob = await ttsResponse.blob();
                                                                                const url = URL.createObjectURL(blob);
                                                                                setChatMsgs(prevMsgs =>
                                                                                    prevMsgs.map(msg =>
                                                                                        msg.id === item.id ? { ...msg, aiTTLURL: url } : msg
                                                                                    )
                                                                                );

                                                                            } catch (err) {
                                                                                console.error("This language is not supported now", err);
                                                                                item.isAiAudioPlaying = false;
                                                                                message.error({ content: "This language is not supported now", key: "tts" });
                                                                            }
                                                                        }}
                                                                        style={{ cursor: 'pointer', textDecoration: 'none', paddingTop: "3px" }}
                                                                    >
                                                                        {IconTextSpeech}
                                                                    </a>


                                                                </div>

                                                                <div>
                                                                    <a
                                                                        href={undefined}
                                                                        onClick={() => {

                                                                            let userContent = item.userMsg
                                                                            let aiContent = item.aiMsg;

                                                                            if (
                                                                                item.userTranslateMsg &&
                                                                                item.userTranslateMsg != ""
                                                                            ) {
                                                                                userContent +=
                                                                                    "\n\n" + item.userTranslateMsg;
                                                                            }

                                                                            if (
                                                                                item.aiTranslateMsg &&
                                                                                item.aiTranslateMsg != ""
                                                                            ) {
                                                                                aiContent +=
                                                                                    "\n\n" + item.aiTranslateMsg;
                                                                            }
                                                                            const message = JSON.stringify({
                                                                                userContent: userContent,
                                                                                aiContent: aiContent,
                                                                            });

                                                                            sendToParent("stickyNotes", message);
                                                                        }}
                                                                    >
                                                                        {IconUnion}
                                                                    </a>


                                                                </div>
                                                                <div>
                                                                    <a
                                                                        href={undefined}
                                                                        onClick={() => removeMsg(item, "ai")}
                                                                    >
                                                                        {IconClose}
                                                                    </a>
                                                                </div>
                                                                <div>
                                                                    <AppAiChatTranslateWin translateResult={(translatedText) => {
                                                                        item.aiTranslateMsg = translatedText
                                                                        handleOnClick(item)
                                                                    }} textValue={item.aiMsg} />
                                                                </div>

                                                            </div>


                                                        </div>
                                                        <div className="item-end"></div>
                                                    </div>
                                                    {item.aiTTLURL && (<div className="ai-show-audio"><audio controls>
                                                        <source src={item.aiTTLURL} type="audio/wav" />
                                                        Your browser does not support the audio element.
                                                    </audio></div>)}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </>
                            );
                        })}
                    </div>

                    <div className="send-box">
                        <div key="ai-chat-input" className="txt-input">
                            {/* ── ASR mic button ───────────────────────────────────── */}
                            <div
                                className={`left-mic-btn${isRecording ? " recording" : ""}`}
                                onClick={handleMicClick}
                                title={isRecording ? "Stop recording" : "Voice input"}
                            >
                                {isTranscribing ? (
                                    <Spinner />
                                ) : isRecording ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
                                        fill="currentColor" stroke="currentColor" strokeWidth="1"
                                        strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="9" y="2" width="6" height="12" rx="3" />
                                        <path d="M5 10a7 7 0 0 0 14 0" fill="none" strokeWidth="2" />
                                        <line x1="12" y1="19" x2="12" y2="22" strokeWidth="2" />
                                        <line x1="9" y1="22" x2="15" y2="22" strokeWidth="2" />
                                    </svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" strokeWidth="2"
                                        strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="9" y="2" width="6" height="12" rx="3" />
                                        <path d="M5 10a7 7 0 0 0 14 0" />
                                        <line x1="12" y1="19" x2="12" y2="22" />
                                        <line x1="9" y1="22" x2="15" y2="22" />
                                    </svg>
                                )}
                            </div>
                            {/* ─────────────────────────────────────────────────────── */}

                            <Input
                                placeholder="Ask a question or generate the following content..."
                                value={chatMsgContent}
                                onChange={(e) => setChatMsgContent(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        sendAiMessage(chatMsgContent);
                                    }
                                }}
                            />

                            <div className="right-send-msg" onClick={() => sendAiMessage(chatMsgContent)}>
                                {IconSendMsgDefault}
                            </div>
                        </div>
                    </div>

                </div>

            </div>)
        }


        const renderAiAnalyzeImage = () => {

            return (<div className="ai-analyze-image">
                <div className="content-main">

                    <div className="message-box">

                        {analyzeMsgs.map((item) => {
                            return (<>
                                {(item.aiMsg != '' || item.userMsg != '') && <div key={`ai_item_${item.id}`} className="item">
                                    {item.imageDataUrlPre && (<div className="item-uploaded-img">
                                        <img data-isicon={true} src={item.imageDataUrlPre} />
                                    </div>)}
                                    {!item.isDelUserMsg && (<div className="item-user">
                                        <div className="buttons">

                                            <div>
                                                <a
                                                    href={undefined}
                                                    onClick={() => removeMsg(item, "user")}
                                                >
                                                    {IconClose}
                                                </a>
                                            </div>
                                        </div>
                                        <div className="content">{item.userMsg}</div>
                                        <div className="avatar">
                                            <div className="icon">I</div>
                                        </div>
                                    </div>)}

                                    {(!item.isDelAiMsg) && (<div className="item-ai">
                                        <div className="avatar">
                                            <div className="icon">AI</div>
                                        </div>

                                        <div className="content">
                                            {(item.aiMsg == '' || !item.aiMsg) ? (<Spinner />) : (<div
                                                dangerouslySetInnerHTML={{
                                                    __html: marked(item.aiMsg) as string,
                                                }}
                                            ></div>)}
                                        </div>
                                        <div className="buttons">

                                            <div>
                                                <a
                                                    href={undefined}
                                                    onClick={() => {
                                                        let userContent = item.userMsg
                                                        let AIContent = item.aiMsg;
                                                        const message = JSON.stringify({
                                                            userContent: userContent,
                                                            aiContent: AIContent,
                                                        });
                                                        sendToParent("stickyNotes", message);
                                                    }}
                                                >
                                                    {IconUnion}
                                                </a>
                                            </div>
                                            <div>
                                                <a
                                                    href={undefined}
                                                    onClick={() => removeMsg(item, "ai")}
                                                >
                                                    {IconClose}
                                                </a>
                                            </div>

                                        </div>
                                    </div>)}


                                </div>}

                            </>)
                        })}
                    </div>

                    <div className="send-box">
                        <div className="send-box-content">
                            <div className="upload-img-pre-list">
                                {analyzeImagePre && (<>

                                    {isUploadImgToServer ? <Spinner /> : <div className="pre-img-item">
                                        <div className="remove-pre-img" onClick={() => {
                                            setAnalyzeImagePre(null)
                                            setAnalyzeUploadImageInfo({ ...analyzeUploadImageInfo, filename: '', subfolder: '', type: '' })
                                        }}>{IconRemovePreImg}</div>
                                        <img data-isicon={true} src={analyzeImagePre} />
                                    </div>}

                                </>)}

                            </div>
                            <TextArea
                                placeholder="Analysis the image given"
                                value={analyzeContent}
                                onChange={(e) => {
                                    setAnalyzeContent(e.target.value)
                                }}
                                onKeyDown={(e) => {


                                }}
                                autoSize={{ maxRows: 10 }}
                            />

                            <div className="send-tools">
                                <div className="tools-left">
                                    <div style={{ cursor: "pointer" }} onClick={() => uploadImage()}>
                                        {IconImageAdd}
                                    </div>
                                </div>
                                <div className="tools-right">
                                    <div style={{ cursor: "pointer" }} onClick={() => sendAiAnalyseImg()}>
                                        {IconSendMsgDefault}
                                    </div>
                                </div>

                            </div>

                        </div>
                    </div>

                </div>

            </div>)
        }

        const renderAiSummarize = () => {
            return (<div className="ai-summarize">
                <div className="content-main">
                    <div className="send-box">
                        <div className="send-box-content">
                            <TextArea
                                placeholder="Ask a question or generate the following content..."
                                value={summarizeOriginInfo}
                                onChange={(e) => {
                                    setSummarizeOriginInfo(e.target.value)
                                }}
                                onKeyDown={(e) => {


                                }}
                                autoSize={{ maxRows: 20 }}
                            />

                            <div className="send-tools">
                                <div className="tools-left">

                                </div>
                                <div className="tools-right">
                                    <div style={{ cursor: "pointer" }} onClick={() => sendAiSummarize()}>
                                        {IconSendMsgDefault}
                                    </div>
                                </div>

                            </div>

                        </div>
                    </div>
                    <div className="message-box">
                        {(isRequestSummarize || summarizeAfterInfo != '') && (<div className="result">
                            <div className="title">Result</div>
                            <div className="content">
                                {summarizeAfterInfo != '' ? (<>
                                    {summarizeAfterInfo}
                                </>) : (<><Spinner /></>)}

                            </div>
                        </div>)}
                    </div>
                </div>
                {summarizeAfterInfo !== "" && (
                    <>
                        <div className="tools-bar-icon">
                            <div>
                                <a
                                    href={undefined}
                                    onClick={() => {
                                        let stickyContent = summarizeAfterInfo;

                                        const data = JSON.stringify({
                                            text: stickyContent,
                                        });
                                        sendToParent("insertToDoc", data);
                                    }}
                                >
                                    {IconUnion}
                                </a>
                            </div>
                        </div>
                    </>
                )}
            </div>)
        }


        const renderAiText2Image = () => {

            return (<div className="ai-text-to-image">
                <div className="content-main">
                    <div className="send-box">
                        <div className="send-box-content">
                            <TextArea
                                placeholder="Ask a question or generate the following content..."
                                value={generateImageResult.prompt}
                                onChange={(e) => {
                                    setGenerateImageResult({
                                        ...generateImageResult,
                                        prompt: e.target.value
                                    })
                                }}
                                onKeyDown={(e) => {


                                }}
                                autoSize={{ maxRows: 20 }}
                            />

                            <div className="send-tools">
                                <div className="tools-left">

                                </div>
                                <div className="tools-right">
                                    <div style={{ cursor: "pointer" }} onClick={() => sendAiGenerateImage()}>
                                        {IconSendMsgDefault}
                                    </div>
                                </div>

                            </div>

                        </div>
                    </div>

                    <div className="message-box">
                        <div className="img-type-box">
                            <div className="title">Style</div>
                            <div className="img-type-list">
                                {PRE_IMAGES.map((item, i) => {
                                    return (<div className="img-type-item" key={`img-type-item${i}`}>
                                        <li className={clsx({
                                            "select-type": item.modeName == generateImageType
                                        })} onClick={() => {
                                            setGenerateImageType(item.modeName || "")
                                        }}>
                                            <img data-isicon={true} src={`/home/ai/${item.imgUrl}`} />
                                            <span>{item.modeName}</span>
                                        </li>
                                    </div>)
                                })}


                            </div>
                        </div>
                        <div className="result">
                            <div className="title">Result</div>
                            <div className="content">

                                {(isRequestGenerateImg || generateImageResult.url) && (<>
                                    {isRequestGenerateImg && (<><Spinner /></>)}
                                    {(generateImageResult.url != '') && (
                                        <>
                                            {
                                                <img style={{ display: generateImageResult.loaded ? 'block' : 'none', maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', objectFit: 'contain' }} onLoad={() => setGenerateImageResult({ ...generateImageResult, loaded: true })} src={`${generateImageResult.url}`} />
                                            }
                                        </>
                                    )}
                                </>)}

                            </div>
                        </div>
                    </div>
                </div>
                {generateImageResult.url !== "" && (
                    <>
                        <div className="tools-bar-icon">
                            <div>
                                <a
                                    href={undefined}
                                    onClick={() => {
                                        const data = JSON.stringify({
                                            url: generateImageResult.url,
                                        });
                                        sendToParent("insertImgToDoc", data);
                                    }}
                                >
                                    {IconUnion}
                                </a>
                            </div>
                        </div>
                    </>
                )}
            </div>)
        }

        const renderAiReadArticle = () => {
            return (<div className="ai-read-article">
                <div className="content-main">
                    <div className="send-box">
                        <div className="send-box-content">
                            <div className="form-item">
                                <label htmlFor="speaker_language">{t("labels.ai_language")}</label>
                                <Select
                                    id="speaker_language"
                                    value={generateAudioResult.language || "en-us"}
                                    placeholder="Select Speaker Language"
                                    style={{
                                        width: '100%',
                                        textAlign: 'left'
                                    }}
                                    dropdownStyle={{
                                        textAlign: 'left'
                                    }}
                                    onChange={(v) => {
                                        const isSupported = SPEAKER_LANGUAGES.some(
                                            (lang) => lang.value === v
                                        );
                                        setGenerateAudioResult((prev) => ({
                                            ...prev,
                                            language: v,
                                        }));
                                        setIsSupportedLanguage(isSupported);
                                    }}
                                    options={[
                                        ...SPEAKER_LANGUAGES.map((c) => ({
                                            label: c.label,
                                            value: c.value,
                                        })),
                                        {
                                            label: "Not supported",
                                            value: "not-supported",
                                            disabled: true,
                                        },
                                    ]}
                                />
                            </div>

                            <div className="form-item-text">
                                <label htmlFor="article">{t("labels.ai_text")}</label>
                                <TextArea
                                    placeholder={t("labels.generate_speech_from_text")}
                                    value={generateAudioResult.input}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setGenerateAudioResult({
                                            ...generateAudioResult,
                                            input: value
                                        })
                                        handleTextChange(value);
                                    }}
                                    onKeyDown={(e) => {


                                    }}
                                />
                            </div>

                            <div className="btn-group">
                                <div className="left">
                                    <div className="btn-clear"
                                        onClick={() =>
                                            setGenerateAudioResult({ input: "", result: "", language: "en-us" })
                                        }
                                    >
                                        {t("labels.ai_clear")}
                                    </div>
                                </div>
                                <div className="right">
                                    <div className={`btn-submit ${!isSupportedLanguage ||
                                        isDetecting ||
                                        generateAudioResult.language === "not-supported"
                                        ? "disabled"
                                        : ""
                                        }`}
                                        onClick={() => {
                                            if (
                                                !isSupportedLanguage ||
                                                isDetecting ||
                                                generateAudioResult.language === "not-supported"
                                            )
                                                return;
                                            sendAiGenerateAudio();
                                        }}
                                    >
                                        {t("labels.ai_generate")}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="message-box">
                        <div className="result">
                            {(isRequestGenerateAudio || generateAudioResult.result !== '') && (
                                <>
                                    <div className="title">{t("labels.result")}</div>
                                    <div className="content">
                                        {isRequestGenerateAudio ? (
                                            <Spinner />
                                        ) : (
                                            <>
                                                <div className="content-list">
                                                    <audio controls>
                                                        <source src={generateAudioResult.result} type="audio/wav" />
                                                        Your browser does not support the audio element.
                                                    </audio>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>


                </div>
                <div className="buttons">
                </div>
            </div>)
        }


        const renderAiTranslate = () => {
            return (<div className="ai-translate">
                <AppAiTranslateAssist
                />
            </div>)
        }



        const renderAiQuizAndAssessment = () => {
            return (<div className="ai-lesson-plan">
                <div className="content-main">
                    <div className="send-box">
                        <div className="send-box-content">
                            <div className="form-item">
                                <label htmlFor="quiz_subject">Subject</label>
                                <Input required id="quiz_subject" placeholder='subject' value={quizInfoPrompt.subject} onChange={(e) => setQuizInfoPrompt({
                                    ...quizInfoPrompt,
                                    subject: e.target.value
                                })} />
                            </div>

                            <div className="form-item">
                                <label htmlFor="quiz_grade">Grade</label>
                                <Select
                                    id="quiz_grade"
                                    value={quizInfoPrompt.grade}
                                    placeholder="Select Grade"
                                    style={{
                                        width: '100%',
                                        textAlign: 'left',

                                    }}
                                    dropdownStyle={{
                                        textAlign: 'left'
                                    }}
                                    onChange={(v) => {
                                        setQuizInfoPrompt({
                                            ...quizInfoPrompt,
                                            grade: v
                                        })
                                    }}
                                    options={grades.map((c: any) => { return { label: c.label, value: c.value } })}
                                />
                            </div>

                            <div className="form-item">
                                <label htmlFor="quiz_topic">Topic</label>
                                <Input required id="quiz_topic" value={quizInfoPrompt.topic} placeholder='topic' onChange={(e) => setQuizInfoPrompt({
                                    ...quizInfoPrompt,
                                    topic: e.target.value
                                })} />
                            </div>

                            <div className="form-item">
                                <label htmlFor="quiz_language">Language</label>
                                <Select
                                    id="quiz_language"
                                    value={quizInfoPrompt.language}
                                    placeholder="Select Language"
                                    style={{
                                        width: '100%',
                                        textAlign: 'left'
                                    }}
                                    dropdownStyle={{
                                        textAlign: 'left'
                                    }}
                                    onChange={(v) => {
                                        setQuizInfoPrompt({
                                            ...quizInfoPrompt,
                                            language: v
                                        })
                                    }}
                                    options={sortedLanguages.map((c: any) => { return { label: c.label, value: c.value } })}
                                />
                            </div>

                            <div className="btn-group">
                                <div className="left">
                                    <div className="btn-clear" onClick={() => {
                                        setQuizInfoPrompt({ topic: '', grade: '', subject: '', language: '', result: '' })
                                        setIsRequestQuiz(false)
                                    }}>Clear</div>
                                </div>
                                <div className="right">
                                    <div className="btn-submit" onClick={() => sendAiQuiz()}>Generate</div>
                                </div>
                            </div>

                        </div>
                    </div>

                    <div className="message-box">
                        <div className="result">

                            {(isRequestQuiz || quizInfoPrompt.result != '') && (<><div className="title">Result</div><div className="content">
                                {quizInfoPrompt.result != '' ? (<>
                                    <div
                                        className="content-list"
                                        dangerouslySetInnerHTML={{
                                            __html: marked(quizInfoPrompt.result || "") as string,
                                        }}
                                    ></div>
                                </>) : (<><Spinner /></>)}
                            </div></>)}

                        </div>

                    </div>

                </div>
                <div className="tools-bar-icon">

                    {quizInfoPrompt.result != '' && (<>
                        <div>
                            <a
                                href={undefined}
                                onClick={() => {
                                    let stickyContent = quizInfoPrompt.result
                                    const message = JSON.stringify({
                                        content: stickyContent,
                                    });
                                    sendToParent("show-in-sticky", message);
                                }}
                            >
                                {IconUnion}
                            </a>
                        </div>
                    </>)}
                </div>
            </div>)
        }


        const renderLearningCompanion = () => {
            let gradesLearning = [...grades, { label: "Professional Staff", value: "Professional Staff" }];

            return (<div className="ai-lesson-plan">
                <div className="content-main">
                    <div className="send-box">
                        <div className="send-box-content">
                            <div className="form-item">
                                <label htmlFor="learning_country">Country</label>
                                <Select
                                    id="learning_country"
                                    value={learningCompanionInfo.country}
                                    placeholder="Select Country"
                                    style={{
                                        width: '100%',
                                        textAlign: 'left'
                                    }}
                                    dropdownStyle={{
                                        textAlign: 'left'
                                    }}
                                    onChange={(v) => {
                                        setLearningCompanionInfo({
                                            ...learningCompanionInfo,
                                            country: v
                                        })
                                    }}
                                    options={sortedCountrys.map((c: any) => { return { label: c.label, value: c.value } })}
                                />
                            </div>
                            <div className="form-item">
                                <label htmlFor="learning_grade">Grade</label>
                                <Select
                                    id="learning_grade"
                                    value={learningCompanionInfo.grade}
                                    placeholder="Select Grade"
                                    style={{
                                        width: '100%',
                                        textAlign: 'left'
                                    }}
                                    dropdownStyle={{
                                        textAlign: 'left'
                                    }}
                                    onChange={(v) => {
                                        setLearningCompanionInfo({
                                            ...learningCompanionInfo,
                                            grade: v
                                        })
                                    }}
                                    options={gradesLearning.map((c: any) => { return { label: c.label, value: c.value } })}
                                />
                            </div>
                            <div className="form-item">
                                <label htmlFor="learning_language">Language</label>
                                <Select
                                    id="learning_language"
                                    value={learningCompanionInfo.language}
                                    placeholder="Select Language"
                                    style={{
                                        width: '100%',
                                        textAlign: 'left'
                                    }}
                                    dropdownStyle={{
                                        textAlign: 'left'
                                    }}
                                    onChange={(v) => {
                                        setLearningCompanionInfo({
                                            ...learningCompanionInfo,
                                            language: v
                                        })
                                    }}
                                    options={sortedLanguages.map((c: any) => { return { label: c.label, value: c.value } })}
                                />
                            </div>
                            <div className="form-item">
                                <label htmlFor="learning_subject">Subject</label>
                                <Input required id="learning_subject" placeholder='subject' value={learningCompanionInfo.subject} onChange={(e) => setLearningCompanionInfo({
                                    ...learningCompanionInfo,
                                    subject: e.target.value
                                })} />
                            </div>



                            <div className="form-item">
                                <label htmlFor="learning_question">Question</label>
                                <Input required id="learning_question" value={learningCompanionInfo.question} placeholder='question' onChange={(e) => setLearningCompanionInfo({
                                    ...learningCompanionInfo,
                                    question: e.target.value
                                })} />
                            </div>



                            <div className="btn-group">
                                <div className="left">
                                    <div className="btn-clear" onClick={() => {
                                        setLearningCompanionInfo({ country: '', question: '', grade: '', subject: '', language: '', result: '' })
                                        setIsRequestLearning(false)
                                    }}>Clear</div>
                                </div>
                                <div className="right">
                                    <div className="btn-submit" onClick={() => sendAiLearningCompanion()}>Generate</div>
                                </div>
                            </div>

                        </div>
                    </div>

                    <div className="message-box">
                        <div className="result">

                            {(isRequestLearning || learningCompanionInfo.result != '') && (<><div className="title">Result</div>
                                <div className="content">
                                    {learningCompanionInfo.result != '' ? (<>
                                        <div
                                            className="content-list"
                                            dangerouslySetInnerHTML={{
                                                __html: marked(learningCompanionInfo.result || "") as string,
                                            }}
                                        ></div>
                                    </>) : (<><Spinner /></>)}
                                </div></>)}


                        </div>


                    </div>

                </div>
                <div className="tools-bar-icon">

                    {learningCompanionInfo.result != '' && (<>

                        <div>
                            <a
                                href={undefined}
                                onClick={() => {

                                    let stickyContent = learningCompanionInfo.result;
                                    const message = JSON.stringify({
                                        content: stickyContent,
                                    });
                                    sendToParent("show-in-sticky", message);
                                }}
                            >
                                {IconUnion}
                            </a>
                        </div>

                    </>)}


                </div>
            </div>)
        }

        const renderVocabularyList = () => {
            return (<div className="ai-lesson-plan">
                <div className="content-main">
                    <div className="send-box">
                        <div className="send-box-content">
                            <div className="form-item">
                                <label htmlFor="quiz_subject">Subject</label>
                                <Input required id="quiz_subject" placeholder='subject' value={vocabularyListInfo.subject} onChange={(e) => setVocabularyListInfo({
                                    ...vocabularyListInfo,
                                    subject: e.target.value
                                })} />
                            </div>

                            <div className="form-item">
                                <label htmlFor="quiz_grade">Grade</label>
                                <Select
                                    id="quiz_grade"
                                    value={vocabularyListInfo.grade}
                                    placeholder="Select Grade"
                                    style={{
                                        width: '100%',
                                        textAlign: 'left'
                                    }}
                                    dropdownStyle={{
                                        textAlign: 'left'
                                    }}
                                    onChange={(v) => {
                                        setVocabularyListInfo({
                                            ...vocabularyListInfo,
                                            grade: v
                                        })
                                    }}
                                    options={grades.map((c: any) => { return { label: c.label, value: c.value } })}
                                />
                            </div>

                            <div className="form-item">
                                <label htmlFor="quiz_topic">Text</label>
                                <Input required id="quiz_topic" value={vocabularyListInfo.text} placeholder='text' onChange={(e) => setVocabularyListInfo({
                                    ...vocabularyListInfo,
                                    text: e.target.value
                                })} />
                            </div>

                            <div className="form-item">
                                <label htmlFor="quiz_language">Language</label>
                                <Select
                                    id="quiz_language"
                                    value={vocabularyListInfo.language}
                                    placeholder="Select Language"
                                    style={{
                                        width: '100%',
                                        textAlign: 'left'
                                    }}
                                    dropdownStyle={{
                                        textAlign: 'left'
                                    }}
                                    onChange={(v) => {
                                        setVocabularyListInfo({
                                            ...vocabularyListInfo,
                                            language: v
                                        })
                                    }}
                                    options={sortedLanguages.map((c: any) => { return { label: c.label, value: c.value } })}
                                />
                            </div>

                            <div className="btn-group">
                                <div className="left">
                                    <div className="btn-clear" onClick={() => {
                                        setVocabularyListInfo({ text: '', grade: '', subject: '', language: '', result: '' })
                                        setIsRequestVocabulary(false)
                                    }}>Clear</div>
                                </div>
                                <div className="right">
                                    <div className="btn-submit" onClick={() => sendAiVocabulary()}>Generate</div>
                                </div>
                            </div>

                        </div>
                    </div>

                    <div className="message-box">
                        <div className="result">

                            {(isRequestVocabulary || vocabularyListInfo.result != '') && (<><div className="title">Result</div>
                                <div className="content">
                                    {vocabularyListInfo.result != '' ? (<>
                                        <div
                                            className="content-list"
                                            dangerouslySetInnerHTML={{
                                                __html: marked(vocabularyListInfo.result || "") as string,
                                            }}
                                        ></div>
                                    </>) : (<><Spinner /></>)}
                                </div></>)}


                        </div>


                    </div>

                </div>
                <div className="tools-bar-icon">
                    {vocabularyListInfo.result != '' && (<>
                        <div>
                            <a
                                href={undefined}
                                onClick={() => {
                                    let stickyContent = vocabularyListInfo.result;
                                    const message = JSON.stringify({
                                        content: stickyContent,
                                    });
                                    sendToParent("show-in-sticky", message);
                                }}
                            >
                                {IconUnion}
                            </a>
                        </div>
                    </>)}
                </div>
            </div>)
        }


        const renderLessonPlan = () => {
            let gradesLearning = [...grades, { label: "Professional Staff", value: "Professional Staff" }];

            return (<div className="ai-lesson-plan">
                <div className="content-main">
                    <div className="send-box">
                        <div className="send-box-content">
                            <div className="form-item">
                                <label htmlFor="learning_country">Country</label>
                                <Select
                                    id="learning_country"
                                    value={lessonPlanInfo.country}
                                    placeholder="Select Country"
                                    style={{
                                        width: '100%',
                                        textAlign: 'left',
                                    }}
                                    dropdownStyle={{
                                        textAlign: 'left'
                                    }}

                                    onChange={(v) => {
                                        setLessonPlanInfo({
                                            ...lessonPlanInfo,
                                            country: v
                                        })
                                    }}
                                    options={sortedCountrys.map((c: any) => { return { label: c.label, value: c.value } })}
                                />
                            </div>
                            <div className="form-item">
                                <label htmlFor="learning_grade">Grade</label>
                                <Select
                                    id="learning_grade"
                                    value={lessonPlanInfo.grade}
                                    placeholder="Select Grade"
                                    style={{
                                        width: '100%',
                                        textAlign: 'left',
                                    }}
                                    dropdownStyle={{
                                        textAlign: 'left',
                                    }}
                                    onChange={(v) => {
                                        setLessonPlanInfo({
                                            ...lessonPlanInfo,
                                            grade: v
                                        })
                                    }}
                                    options={gradesLearning.map((c: any) => { return { label: c.label, value: c.value } })}
                                />
                            </div>
                            <div className="form-item">
                                <label htmlFor="learning_language">Language</label>
                                <Select
                                    id="learning_language"
                                    value={lessonPlanInfo.language}
                                    placeholder="Select Language"
                                    style={{
                                        width: '100%',
                                        textAlign: 'left',
                                        fontSize: "6px"
                                    }}
                                    dropdownStyle={{
                                        textAlign: 'left',
                                        fontSize: "6px",
                                    }}
                                    onChange={(v) => {
                                        setLessonPlanInfo({
                                            ...lessonPlanInfo,
                                            language: v
                                        })
                                    }}
                                    options={sortedLanguages.map((c: any) => { return { label: c.label, value: c.value } })}
                                />
                            </div>
                            <div className="form-item">
                                <label htmlFor="learning_subject">Subject</label>
                                <Input required id="learning_subject" placeholder='subject' value={lessonPlanInfo.subject} onChange={(e) => setLessonPlanInfo({
                                    ...lessonPlanInfo,
                                    subject: e.target.value
                                })} />
                            </div>



                            <div className="form-item">
                                <label htmlFor="learning_question">Question</label>
                                <Input required id="learning_question" value={lessonPlanInfo.question} placeholder='question' onChange={(e) => setLessonPlanInfo({
                                    ...lessonPlanInfo,
                                    question: e.target.value
                                })} />
                            </div>



                            <div className="btn-group">
                                <div className="left">
                                    <div className="btn-clear" onClick={() => {
                                        setLessonPlanInfo({ country: '', question: '', grade: '', subject: '', language: '', result: '' })
                                        setIsRequestLessonPlan(false)
                                    }}>Clear</div>
                                </div>
                                <div className="right">
                                    <div className="btn-submit" onClick={() => sendAiLessionPlan()}>Generate</div>
                                </div>
                            </div>

                        </div>
                    </div>

                    <div className="message-box">
                        <div className="result">

                            {(isRequestLessonPlan || lessonPlanInfo.result !== '') && (<><div className="title">Result</div>
                                <div className="content">
                                    {lessonPlanInfo.result !== '' ? (<>
                                        <div
                                            className="content-list"
                                            dangerouslySetInnerHTML={{
                                                __html: marked(lessonPlanInfo.result || "") as string,
                                            }}
                                        ></div>
                                    </>) : (<><Spinner /></>)}
                                </div></>)}


                        </div>


                    </div>

                </div>
                {lessonPlanInfo.result !== '' && (<>
                    <div className="tools-bar-icon">
                        <div>
                            <a
                                href={undefined}
                                onClick={() => {
                                    let stickyContent = lessonPlanInfo.result;
                                    const message = JSON.stringify({
                                        content: stickyContent,
                                    });
                                    sendToParent("show-in-sticky", message);
                                }}
                            >
                                {IconUnion}
                            </a>
                        </div>
                    </div>
                </>)}



            </div>)
        }
        return (

            <div className="imago">

                <div
                    className="app-right-side-container"
                >
                    <div className="side-box-header">
                        <div className="header-left">
                            <div className={`go-history${sideOpenState.openRightSidebar === "ai_assist" ? " hidden" : ""}`} onClick={() => {
                                setIframeQuizHasLoaded(false)
                                setSideOpenState({ openRightSidebar: "ai_assist" });
                                requestAnimationFrame(() => {
                                    requestAnimationFrame(() => {
                                        setTimeout(() => {
                                            if (aiAssistScrollRef.current) {
                                                aiAssistScrollRef.current.scrollTop = listScrollPos.current;
                                            }
                                        }, 80);
                                    });
                                });
                            }}>
                                {(sideOpenState.openRightSidebar && sideOpenState.openRightSidebar !== "ai_assist") && (<div className="icon">
                                    {IconLeftGoHistory}
                                </div>)}
                            </div>

                            <div className="title">
                                {sidebarTitles[sideOpenState.openRightSidebar] ?? ""}
                            </div>

                        </div>
                        <div onClick={() => {
                            const data = JSON.stringify({});
                            sendToParent("closeAiAssistWin", data);
                        }} className="close">
                            {IconCloseRightSideWin}
                        </div>
                    </div>
                    <div className="side-box-content" ref={sideContentRef}>
                        <Suspense fallback={<Spinner />}>
                            {sideOpenState.openRightSidebar === "ai_assist" ? (
                                renderAiAssist()
                            ) : sideOpenState.openRightSidebar === "ai_generate_image" ? (
                                renderAiText2Image()
                            ) : sideOpenState.openRightSidebar === "ai_summarize" ? (
                                renderAiSummarize()
                            ) : sideOpenState.openRightSidebar === "ai_analyze_image" ? (
                                renderAiAnalyzeImage()
                            ) : sideOpenState.openRightSidebar === "ai_prompt_text" ? (
                                renderAiChat()
                            ) : sideOpenState.openRightSidebar === "ai_read_article" ? (
                                renderAiReadArticle()
                            ) : sideOpenState.openRightSidebar === "ai_quiz_and_assessment" ? (
                                renderAiQuizAndAssessment()
                            ) : sideOpenState.openRightSidebar === "ai_vocabulary_list" ? (
                                renderVocabularyList()
                            ) : sideOpenState.openRightSidebar === "ai_social_story" ? (
                                <AISocailStory token={`${token}`} sendToParent={sendToParent} />
                            ) : sideOpenState.openRightSidebar === "ai_worksheet_generator" ? (
                                <AiWorkSheet token={`${token}`} sendToParent={sendToParent} />
                            ) : sideOpenState.openRightSidebar === "ai_sports_practice" ? (
                                <AiSportsPractice token={`${token}`} sendToParent={sendToParent} />
                            ) : sideOpenState.openRightSidebar === "ai_teacher_joke" ? (
                                <AITeacherJoke token={`${token}`} sendToParent={sendToParent} />
                            ) : sideOpenState.openRightSidebar === "ai_math_story" ? (
                                <AiMathStory token={`${token}`} sendToParent={sendToParent} />
                            ) : sideOpenState.openRightSidebar === "ai_rubric_generator" ? (
                                <AiRubric token={`${token}`} sendToParent={sendToParent} />
                            ) : sideOpenState.openRightSidebar === "ai_science_teaching" ? (
                                <AiScienceTeaching token={`${token}`} sendToParent={sendToParent} />
                            ) : sideOpenState.openRightSidebar === "ai_text_leveler" ? (
                                <AiTextLeveler token={`${token}`} sendToParent={sendToParent} />
                            ) : sideOpenState.openRightSidebar === "ai_grammer_checker" ? (
                                <AiGrammerChecker token={`${token}`} sendToParent={sendToParent} />
                            ) : sideOpenState.openRightSidebar === "ai_historian_question" ? (
                                <AiHistorianQuestion token={`${token}`} sendToParent={sendToParent} />
                            ) : sideOpenState.openRightSidebar === "ai_language_tutor" ? (
                                <AiLanguageTutor token={`${token}`} sendToParent={sendToParent} />
                            ) : sideOpenState.openRightSidebar === "ai_translate_file" ? (
                                <AiTranslateFile token={`${token}`} sendToParent={sendToParent} />
                            ) : sideOpenState.openRightSidebar === "ai_research_agent" ? (
                                <AiResearchAgent token={`${token}`} sendToParent={sendToParent} />
                            ) : sideOpenState.openRightSidebar === "ai_blog_generator" ? (
                                <AiBlogGenerator token={`${token}`} sendToParent={sendToParent} />
                            ) : sideOpenState.openRightSidebar === "ai_strategic_consulting" ? (
                                <AiStrategicConsulting token={`${token}`} sendToParent={sendToParent}/>
                            ) : sideOpenState.openRightSidebar === "ai_educational_file_summariser" ? (
                                <AiEducationalFileSummariser token={`${token}`} sendToParent={sendToParent}/>
                            ) : sideOpenState.openRightSidebar === "ai_lesson_minutes_summary" ? (
                                <AiLessonMinutesSummary token={`${token}`} sendToParent={sendToParent}/>
                            ) : sideOpenState.openRightSidebar === "ai_science_lab_designer" ? (
                                <AiScienceLabDesigner token={`${token}`} sendToParent={sendToParent}/>
                            ) : sideOpenState.openRightSidebar === "ai_academic_content" ? (
                                <AiAcademicContent token={`${token}`} sendToParent={sendToParent}/>
                            // ) : sideOpenState.openRightSidebar === "ai_youtube_data_analysis" ? (
                            //     <AiYoutubeDataAnalysis token={`${token}`} sendToParent={sendToParent}/>
                            ) : sideOpenState.openRightSidebar === "ai_lesson_plan" ? (
                                renderLessonPlan()
                            ) : sideOpenState.openRightSidebar === "ai_learning_companion" ? (
                                renderLearningCompanion()
                            ) : sideOpenState.openRightSidebar === "ai_translate" ? (
                                renderAiTranslate()
                            ) : sideOpenState.openRightSidebar === "ai_diagram" ? (
                                <AiDiagram token={`${token}`} sendToParent={sendToParent} />
                            ) : sideOpenState.openRightSidebar === "ai_report_card_comment" ? (
                                <AiReportCardComment token={`${token}`} sendToParent={sendToParent} />
                            ) : sideOpenState.openRightSidebar === "ai_project_based_learning" ? (
                                <AiProjectBasedLearning token={`${token}`} sendToParent={sendToParent} />
                            ) : sideOpenState.openRightSidebar === "ai_sat_ela_practice_test" ? (
                                <AiSatElaPracticeTest token={`${token}`} sendToParent={sendToParent} />
                            ) : sideOpenState.openRightSidebar === "ai_blog_inline" ? (
                                <AiBlogInline token={`${token}`} sendToParent={sendToParent} />
                            ) : (
                                <></>
                            )}
                        </Suspense>
                    </div>
                </div>
            </div>

        );
    };